import { Box, Button, IconButton, Paper, Stack, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import { useState } from "react";
import { getPreSignedUrl } from "../../api/docs";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: ImageBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const ImageBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const fileName = `page-images/${uuidv4()}-${file.name}`;
            const preSignedUrl = await getPreSignedUrl(fileName, file.type);
            
            await fetch(preSignedUrl, {
                method: "PUT",
                body: file,
                headers: {
                    "Content-Type": file.type,
                },
            });

            const imageUrl = `https://riverhawkoa-docs.s3.amazonaws.com/${fileName}`;
            onChange({
                ...block.content,
                url: imageUrl,
            });
        } catch (error) {
            console.error("Failed to upload image:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Stack spacing={2}>
                        {block.content.url ? (
                            <Box>
                                <img
                                    src={block.content.url}
                                    alt={block.content.alt}
                                    style={{ maxWidth: "100%", height: "auto" }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    border: "2px dashed #ccc",
                                    borderRadius: 1,
                                    p: 4,
                                    textAlign: "center",
                                }}
                            >
                                <ImageIcon sx={{ fontSize: 48, color: "#ccc" }} />
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploading}
                        >
                            {uploading ? "Uploading..." : "Upload Image"}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </Button>
                        <TextField
                            label="Alt Text"
                            value={block.content.alt}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    alt: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Caption (optional)"
                            value={block.content.caption || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    caption: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default ImageBlockEditor;
